import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';

import {
  NgbCarousel,
  NgbCarouselConfig,
  NgbCarouselModule,
  NgbSlide
} from '@ng-bootstrap/ng-bootstrap';

import { StatsigService } from 'app/shared/services/statsig.service';

import { SvgIconComponent } from '../../shared/components/svg-icon/svg-icon.component';
import { VideoComponent } from 'app/shared/components/video/video.component';
import { ThumbnailLoaderComponent } from '../image-generation/thumbnail-loader/thumbnail-loader.component';
import { WatermarkComponent } from '../../shared/components/watermark/watermark.component';
import { FeedbackControlsComponent } from '../feedback-controls/feedback-controls.component';

import { MediaPreviewCloseDirective } from '../../shared/directives/media-preview-close.directive';

import { IWatermarkConfig } from '../../shared/interfaces/media.interfaces';
import { ISvgConfig } from '../../shared/interfaces/svg.interfaces';
import { ICreatorContent, ICreatorData } from '../../interfaces/creator.interfaces';
import { IGenerateImage } from '../../shared/interfaces/media.interfaces';
import { IDiscoverItem } from '../../interfaces/discover.interfaces';

import { ESvgTypes } from '../../shared/enums/svg.enums';
import { ECreatorMediaTypes, ECreatorVideoTypes } from '../../enums/creator.enums';
import { ECarouselNavigation, EMediaLightboxUsage } from '../../shared/enums/shared.enums';
import { EFeatureGates } from 'app/shared/enums/feature-gates.enums';

@Component({
  selector: 'stxt-media-lightbox',
  standalone: true,
  imports: [
    NgbCarouselModule,
    CommonModule,
    NgbCarousel,
    NgbSlide,
    SvgIconComponent,
    VideoComponent,
    ThumbnailLoaderComponent,
    WatermarkComponent,
    FeedbackControlsComponent,
    MediaPreviewCloseDirective
  ],
  templateUrl: './media-lightbox.component.html',
  styleUrl: './media-lightbox.component.scss'
})
export class MediaLightboxComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() mediaList: ICreatorContent[];
  @Input() generatedMediaList: IGenerateImage[];
  @Input() usageCase: EMediaLightboxUsage;
  @Input() creator?: ICreatorData;
  @Input() selectedMedia?: ICreatorContent;
  @Input() selectedGeneratedMedia?: IGenerateImage;
  @Input() postDescription: string;
  @Input() creators?: IDiscoverItem[];
  @Output() closeFullScreen: EventEmitter<void> = new EventEmitter<void>();
  @Output() regenerateImage: EventEmitter<IGenerateImage> = new EventEmitter<IGenerateImage>();
  @ViewChild(VideoComponent) videoComponent?: VideoComponent;

  @ViewChild('carousel') set carouselRef(carousel: NgbCarousel) {
    if (carousel) {
      this.carousel = carousel;
    }
  }

  private carousel: NgbCarousel;
  public imagesToDisplay: IGenerateImage[] | ICreatorContent[];
  public closeSvg: ISvgConfig = { fill: ESvgTypes.OutlinedDark, name: 'close' };
  public leftNavigation: ISvgConfig = { fill: ESvgTypes.OutlinedDark, name: 'arrow_left' };
  public rightNavigation: ISvgConfig = { fill: ESvgTypes.OutlinedDark, name: 'arrow_right' };
  public tokenIcon: ISvgConfig = { fill: ESvgTypes.None, name: 'token-badge' };
  public isFeedbackControlsEnabled: boolean;
  protected readonly ECreatorMediaTypes = ECreatorMediaTypes;
  protected readonly ECreatorVideoTypes = ECreatorVideoTypes;
  protected readonly ECarouselNavigation = ECarouselNavigation;
  protected readonly EMediaLightboxUsage = EMediaLightboxUsage;

  constructor(
    config: NgbCarouselConfig,
    private readonly cdr: ChangeDetectorRef,
    private readonly statsigService: StatsigService
  ) {
    config.wrap = false;
    config.interval = 0;
    config.keyboard = false;
    config.animation = false;
  }

  ngOnInit(): void {
    this.checkStatsigFeatureGate(EFeatureGates.FEEDBACK_CONTROLS_MEDIA);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mediaList'] || changes['generatedMediaList']) {
      this.imagesToDisplay =
        this.usageCase === EMediaLightboxUsage.MasonryGallery
          ? this.mediaList
          : this.generatedMediaList;
    }
  }

  ngAfterViewInit(): void {
    const mediaId: string =
      this.usageCase === EMediaLightboxUsage.MasonryGallery
        ? this.selectedMedia?.media_id
        : this.selectedGeneratedMedia?.id;

    if (this.carousel && mediaId) {
      this.carousel.select(`ngb-slide-${this.getSafeSlideId(mediaId)}`);
    }
    this.cdr.detectChanges();
  }

  async checkStatsigFeatureGate(featureGate: EFeatureGates): Promise<void> {
    await this.statsigService.waitForInitialization();
    this.isFeedbackControlsEnabled = await this.statsigService.checkFeatureGate(featureGate);
  }

  carouselNavigation(control: ECarouselNavigation, media: ICreatorContent): void {
    this.checkElementType(media);
    switch (control) {
      case ECarouselNavigation.Previous:
        this.carousel.prev();
        break;
      case ECarouselNavigation.Next:
        this.carousel.next();
        break;
      default:
        console.warn(`Invalid navigation control: ${control}`);
    }
  }

  onRegenerateImage(): void {
    this.regenerateImage.emit(this.selectedGeneratedMedia);
  }

  checkElementType(media: ICreatorContent): void {
    if (media.type !== ECreatorMediaTypes.Video) return;
    if (this.videoComponent && typeof this.videoComponent.togglePlayPause === 'function') {
      this.videoComponent.togglePlayPause(new Event('click'));
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.carousel) return;

    switch (event.key) {
      case 'ArrowLeft':
        this.handleArrowKeyPress(event);
        this.carousel.prev();
        break;

      case 'ArrowRight':
        this.handleArrowKeyPress(event);
        this.carousel.next();
        break;

      case 'Escape':
        event.preventDefault();
        this.closeFullScreen.emit();
        break;
    }
  }

  onOverlayClick(): void {
    this.closeFullScreen.emit();
  }

  private handleArrowKeyPress(event: KeyboardEvent): void {
    event.preventDefault();
    const currentActiveMedia = this.getCurrentActiveMedia();
    if (
      this.usageCase === EMediaLightboxUsage.MasonryGallery &&
      currentActiveMedia &&
      'type' in currentActiveMedia
    ) {
      this.checkElementType(currentActiveMedia as ICreatorContent);
    }
  }

  private getCurrentActiveMedia(): ICreatorContent | IGenerateImage | undefined {
    if (!this.carousel || !this.carousel.activeId || !this.imagesToDisplay) {
      return undefined;
    }

    const activeSlideId = this.carousel.activeId;
    const activeMediaId = activeSlideId.replace('ngb-slide-', '');
    if (!activeMediaId) {
      return undefined;
    }

    return this.imagesToDisplay.find(media => {
      const idToCompare: string =
        this.usageCase === EMediaLightboxUsage.MasonryGallery
          ? (media as ICreatorContent).media_id
          : (media as IGenerateImage).id;
      return this.getSafeSlideId(idToCompare) === activeMediaId;
    });
  }

  getWatermarkConfig(media?: ICreatorContent | IGenerateImage): IWatermarkConfig {
    let creatorName = '';
    let creatorId = '';

    if (this.usageCase === EMediaLightboxUsage.MasonryGallery) {
      creatorName = this.creator?.name || '';
      creatorId = this.creator?.id || '';
    } else if (this.usageCase === EMediaLightboxUsage.ImageGeneration && media && this.creators) {
      const generatedImage = media as IGenerateImage;
      const imageCreator = this.creators.find(c => c.id === generatedImage.creator_id);
      creatorName = imageCreator?.name || '';
      creatorId = imageCreator?.id || '';
    }

    return {
      creatorName,
      creatorId,
      showWatermark: true
    };
  }

  /**
   * Creates a safe ID for carousel slides by removing special characters and using only alphanumeric characters
   * @param id The original ID that may contain special characters
   * @returns A safe ID string that can be used as a DOM element ID
   */
  getSafeSlideId(id: string): string {
    // If the ID is a URL, extract just the filename
    if (id.includes('/')) {
      id = id.split('/').pop() || id;
    }

    // Remove the query string if present
    if (id.includes('?')) {
      id = id.split('?')[0];
    }

    // Remove any file extension
    if (id.includes('.')) {
      id = id.split('.')[0];
    }

    // Replace any remaining special characters with dashes and ensure it's a valid ID
    return id.replaceAll(/[^a-zA-Z0-9-]/g, '-');
  }
}
